import type { ITpvType } from "@apis/sales/types"
import { useGetTpvTypesQuery } from "@apis/sales/useSales"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { Loader2 } from "lucide-react"
import { useEffect } from "react"

type TTpvTypesSelectProps = {
  placeholder?: string
  tpvType: string | null
  setTpvType?: (type: string) => void
  setTpv?: (tpv: ITpvType) => void
}

export const TpvTypesSelect = ({
  placeholder = "Select TPV Type",
  tpvType,
  setTpvType,
  setTpv,
}: TTpvTypesSelectProps) => {
  const { data, isError, error, isPending } = useGetTpvTypesQuery({
    active: true,
  })

  useEffect(() => {
    if (!data?.length) return

    const selectedTpv =
      (tpvType && data.find((tpv: ITpvType) => tpv.vendor_type === tpvType)) ||
      data.find((tpv: ITpvType) => tpv.vendor_type === "Internal") ||
      data[0]

    if (selectedTpv) {
      setTpvType?.(selectedTpv.vendor_type)
      setTpv?.(selectedTpv)
    }
  }, [data, tpvType, setTpvType, setTpv])

  const handleTpvChange = (tpv: ITpvType) => {
    setTpvType?.(tpv.vendor_type)
    setTpv?.(tpv)
  }

  if (isPending) {
    return (
      <div className="tw:flex tw:justify-center">
        <Loader2 className="tw:h-6 tw:w-6 tw:animate-spin tw:text-gray-500" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="tw:text-red-500">
        Error loading tpv vendor types. {error?.message}
      </div>
    )
  }

  return (
    <div>
      <Select
        value={tpvType ?? ""}
        onValueChange={(val) => {
          const tpvObj = data?.find((tpv: ITpvType) => tpv.vendor_type === val)
          if (tpvObj) handleTpvChange(tpvObj)
        }}
      >
        <SelectTrigger>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {data.map((tpv: ITpvType) => (
            <SelectItem key={tpv.vendor_tpv_type_id} value={tpv.vendor_type}>
              {tpv.vendor_type}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
